// ProjectCard - Proje karti component
// Icon + Title + Cycle + Categories + Progress + Stats + Last update
import { Briefcase, Clock, Coins, Users } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { Project } from '../../types'
import ProgressBar from '../ui/ProgressBar'

interface ProjectCardProps {
  project: Project
}

export default function ProjectCard({ project }: ProjectCardProps) {
  const navigate = useNavigate()

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) {return 'Az once'}
    if (diffMins < 60) {return `${diffMins} dk once`}

    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) {return `${diffHours} saat once`}

    const diffDays = Math.floor(diffHours / 24)
    return `${diffDays} gun once`
  }

  return (
    <div
      onClick={() => navigate(`/project/${project.id}`)}
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border-default)',
        borderRadius: 12,
        padding: 20,
        cursor: 'pointer',
        transition: 'all .2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'var(--card-hover-transform)'
        e.currentTarget.style.boxShadow = 'var(--card-hover-shadow)'
        e.currentTarget.style.borderColor = 'var(--primary-blue)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
        e.currentTarget.style.borderColor = 'var(--border-default)'
      }}
    >
      {/* Header - Icon + Title */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Briefcase size={20} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h3
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: 4,
              lineHeight: 1.3,
            }}
          >
            {project.title}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
            <span>Cycle {project.current_cycle}</span>
            <span>•</span>
            <span>{project.primary_categories?.join(', ') || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <ProgressBar progress={project.progress || 0} />

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 12,
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid var(--border-default)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Coins size={14} color="var(--yellow)" />
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {project.budget_flz?.toLocaleString() || '0'} FLZ
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Users size={14} color="var(--green)" />
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {project.expert_count || 0} Uzman
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Clock size={14} color="var(--text-tertiary)" />
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {formatTimeAgo(project.updated_at || project.created_at || '')}
          </span>
        </div>
      </div>
    </div>
  )
}
